import { useCallback, useMemo, useState } from 'react';
import { useApiResource } from '../../utils/useApiResource';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { ApiClient } from '../../utils/apiClient';
import { projectsDataService } from '../../services/projectsDataService';
import { commonDataService } from '../../services/commonDataService';
import { usePermissions } from '../../contexts/AuthContext';
import { P } from '../../utils/permissions';
import type { Project } from '../../components/projects-management/types';
import type { Employee } from '../../components/people-management/types';
import { normaliseEmployeeStatus } from '../../components/people-management/hooks/usePeopleFilters';
import {
  employeeDisplayName,
  employeeNameHaystack,
} from '../../utils/employeeName';

/** Max rows rendered per group; the true total drives the "+N more" hint. */
export const SEARCH_GROUP_CAP = 6;
/** Client-side filter debounce (ms); exported so tests can drive the window. */
export const DEBOUNCE_MS = 200;

export type SearchGroup = 'Projects' | 'People';

export interface SearchOption {
  group: SearchGroup;
  type: 'project' | 'person';
  id: string;
  primary: string;
  secondary: string;
  /** Project results only: the company whose logo, icon or colour marks the row. */
  company?: Project['company'];
  /** Person results only: the profile photo, when they have one. */
  avatarUrl?: string;
}

export interface GlobalSearchState {
  query: string;
  setQuery: (q: string) => void;
  /** Call on first focus to lazily load (and session-cache) the lists. */
  activate: () => void;
  loading: boolean;
  options: SearchOption[];
  projectsTotal: number;
  peopleTotal: number;
}

interface EmployeesResponse {
  data: Employee[];
}

const fullName = (e: Employee): string => employeeDisplayName(e);

/**
 * Pure result builder — exported for unit testing. Filters projects (gated by
 * `canViewProjects`) and the active+hired people pool by the query, caps each
 * group, and returns them grouped (projects first, then people) plus the
 * pre-cap totals that drive the "+N more" hint.
 */
export function buildSearchResults(
  rawQuery: string,
  projects: Project[],
  people: Employee[],
  canViewProjects: boolean,
): { options: SearchOption[]; projectsTotal: number; peopleTotal: number } {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return { options: [], projectsTotal: 0, peopleTotal: 0 };

  const projectMatches = (canViewProjects ? projects : []).filter(
    (p) =>
      p.name.toLowerCase().includes(q) ||
      (p.company?.name ?? '').toLowerCase().includes(q),
  );

  const peopleMatches = people
    .filter((e) => {
      const status = normaliseEmployeeStatus(e.status);
      return status === 'active' || status === 'hired';
    })
    .filter((e) =>
      [employeeNameHaystack(e), e.department, e.position]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );

  const projectOptions: SearchOption[] = projectMatches
    .slice(0, SEARCH_GROUP_CAP)
    .map((p) => ({
      group: 'Projects',
      type: 'project',
      id: p.id,
      primary: p.name,
      secondary: p.company?.name ?? '',
      company: p.company,
    }));

  const peopleOptions: SearchOption[] = peopleMatches
    .slice(0, SEARCH_GROUP_CAP)
    .map((e) => ({
      group: 'People',
      type: 'person',
      id: e.id,
      primary: fullName(e),
      secondary: [e.position, e.department].filter(Boolean).join(' · '),
      avatarUrl: e.avatarUrl,
    }));

  return {
    options: [...projectOptions, ...peopleOptions],
    projectsTotal: projectMatches.length,
    peopleTotal: peopleMatches.length,
  };
}

/**
 * Global top-bar search over Projects + People.
 *
 * - Lists are fetched lazily on first focus (`activate()`) and served from the
 *   shared 60s dedupe/TTL caches (`projectsDataService` / `commonDataService`);
 *   filtering is client-side and debounced — no per-keystroke network hit, no
 *   new backend endpoint.
 * - Project results are gated on `VIEW_PROJECTS`: without it the projects fetch
 *   is skipped and no Projects group is produced (consistent with the hidden
 *   Projects nav item). People are always searchable (Directory is always
 *   visible) but limited to the active + hired pool the Directory shows, so a
 *   person result always resolves to a visible card.
 */
export function useGlobalSearch(): GlobalSearchState {
  const { hasPermission } = usePermissions();
  const canViewProjects = hasPermission(P.VIEW_PROJECTS);

  const [enabled, setEnabled] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, DEBOUNCE_MS);

  const projectsFetcher = useCallback(
    (client: ApiClient) => projectsDataService.projects.get(client),
    [],
  );
  const peopleFetcher = useCallback(
    (client: ApiClient) =>
      commonDataService.employeesNoAbsences.get(client) as Promise<EmployeesResponse>,
    [],
  );

  const projectsRes = useApiResource<Project[]>(projectsFetcher, [], {
    skip: !enabled || !canViewProjects,
    errorPrefix: 'Search failed',
  });
  const peopleRes = useApiResource<EmployeesResponse>(peopleFetcher, [], {
    skip: !enabled,
    errorPrefix: 'Search failed',
  });

  const activate = useCallback(() => setEnabled(true), []);

  const { options, projectsTotal, peopleTotal } = useMemo(
    () =>
      buildSearchResults(
        debounced,
        projectsRes.data ?? [],
        peopleRes.data?.data ?? [],
        canViewProjects,
      ),
    [debounced, canViewProjects, projectsRes.data, peopleRes.data],
  );

  const loading = enabled && (projectsRes.loading || peopleRes.loading);

  return { query, setQuery, activate, loading, options, projectsTotal, peopleTotal };
}
